import { Link } from "react-router-dom";
import { Lock, Mountain, Tent, MapPin, Flag } from "lucide-react";

/**
 * LockedTrailScreen — full-stage replacement shown to free users when they
 * try to access Trail 2 / Trail 3 / Summit on any expedition AFTER their
 * first full expedition has been completed.
 *
 * Shows a mountain visual with the locked stages behind a gold barrier and
 * the upgrade copy. Their progress on completed stages is NOT deleted —
 * the parent ExpeditionView still shows base_camp + trail_1 normally.
 */
const STAGES = [
  { name: "base_camp", label: "Base Camp", icon: Tent, locked: false },
  { name: "trail_1",   label: "Trail 1",   icon: MapPin, locked: false },
  { name: "trail_2",   label: "Trail 2",   icon: MapPin, locked: true },
  { name: "trail_3",   label: "Trail 3",   icon: MapPin, locked: true },
  { name: "summit",    label: "Summit",    icon: Flag,   locked: true },
];

const LockedTrailScreen = ({ stageName }) => {
  return (
    <div className="text-center" data-testid="locked-trail-screen">
      <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Trail locked</div>
      <h2 className="font-heading text-2xl sm:text-3xl font-black text-mm-navy mt-1">
        You've reached the end of your free trail
      </h2>
      <p className="text-mm-navy/80 mt-3 max-w-xl mx-auto leading-relaxed">
        Upgrade to Summit Pass for $5/month to keep climbing all the way to the Summit! 🏔️
      </p>

      {/* Mountain visual — 5 stages, last 3 behind a gold barrier */}
      <div className="mt-7 relative bg-gradient-to-b from-mm-parchment to-white border border-mm-navy/10 rounded-2xl p-5">
        <div className="flex items-end justify-around gap-2 pb-2 min-h-[180px]">
          {STAGES.map((s, i) => {
            const Icon = s.icon;
            // Each stage is taller — visual mountain ascent
            const heights = ["h-16", "h-20", "h-28", "h-36", "h-44"];
            const isLocked = s.locked;
            return (
              <div key={s.name} className="flex flex-col items-center gap-2 flex-1">
                <div className={`relative ${heights[i]} w-full rounded-t-2xl flex flex-col items-center justify-end pb-2 ${isLocked ? "bg-mm-navy/15" : "bg-mm-steel/25"}`}>
                  {isLocked && (
                    <div className="absolute inset-0 flex items-center justify-center" data-testid={`locked-stage-icon-${s.name}`}>
                      <Lock size={20} strokeWidth={2.5} className="text-mm-gold drop-shadow" />
                    </div>
                  )}
                  {!isLocked && (
                    <Icon size={18} strokeWidth={2.5} className="text-mm-navy" />
                  )}
                </div>
                <div className={`text-[10px] uppercase tracking-widest font-bold ${isLocked ? "text-mm-navy/40" : "text-mm-navy"}`}>
                  {s.label}
                </div>
              </div>
            );
          })}
        </div>

        {/* Gold barrier line cutting across the locked trio */}
        <div
          className="absolute inset-y-0 right-0 border-l-4 border-mm-gold pointer-events-none"
          style={{ width: "60%", borderLeftStyle: "dashed", left: "40%" }}
          aria-hidden
        />
      </div>

      <Link
        to="/pricing"
        className="btn-trail btn-gold mt-6 inline-flex"
        data-testid="locked-trail-upgrade-btn"
      >
        Upgrade Now
      </Link>

      <p className="text-xs text-mm-navy/60 mt-3 max-w-md mx-auto" data-testid="locked-trail-progress-saved">
        Your progress is saved — every stage you've already conquered will be waiting for you when you upgrade. 🧭
      </p>
    </div>
  );
};

export default LockedTrailScreen;
